import Sortable from 'sortablejs'

// Keep track of bound handlers to prevent duplicates
let boundHandlers = new Set();

// One-time event bindings that don't need to be reinitialized
function setupEventListeners() {
  // Handle drag and drop events with delegation
  const handleDragOver = (e) => {
    const dropArea = e.target.closest("#drop-area");
    if (dropArea) {
      e.preventDefault();
      dropArea.classList.add("bg-blue-800");
    }
  };

  const handleDragLeave = (e) => {
    const dropArea = e.target.closest("#drop-area");
    if (dropArea) {
      dropArea.classList.remove("bg-blue-800");
    }
  };

  const handleDrop = async (e) => {
    const dropArea = e.target.closest("#drop-area");
    if (!dropArea) return;

    e.preventDefault();
    dropArea.classList.remove("bg-blue-800");

    const dt = e.dataTransfer;
    const uri = dt.getData("text/uri-list");
    const html = dt.getData("text/html");
    let imageUrl = null;

    // 1. Use direct image link if available
    if (uri && uri.match(/\.(jpe?g|png|gif|webp)$/i)) {
      imageUrl = uri;
    }

    // 2. Parse HTML to extract <img src="...">
    if (!imageUrl && html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const img = doc.querySelector("img");
      if (img && img.src) {
        imageUrl = img.src;
      }
    }

    if (!imageUrl || !imageUrl.startsWith("http")) {
      alert("That doesn't look like a valid image.");
      return;
    }

    const token = document.querySelector('meta[name="csrf-token"]').getAttribute("content");
    const formData = new FormData();
    formData.append("image_url", imageUrl);

    const response = await fetch(dropArea.dataset.attachUrl, {
      method: "POST",
      headers: { "X-CSRF-Token": token },
      body: formData
    });
    if (response.ok) {
      const html = await response.text();
      const temp = document.createElement("template");
      temp.innerHTML = html;
      const newItem = temp.content.querySelector("li.sortable-item");
      if (newItem) {
        const el = document.getElementById('sortable-images');
        el.appendChild(newItem);
        // Reinitialize sortable after adding new item
        initializeSortable();
        
        // Wait a moment for the DOM to update
        setTimeout(() => {
          // Find the new dimension element and start polling
          const newDimensionElement = newItem.querySelector('[data-image-dimensions]');
          if (newDimensionElement) {
            pollForDimensions(newDimensionElement).catch(error => {
              console.error('Error polling for dimensions:', error);
            });
          }
        }, 100);
      }
    } else {
      alert("Upload failed.");
    }
  };

  // Handle image paste from clipboard
  const handlePaste = async (e) => {
    const dropArea = document.getElementById("drop-area");
    if (!dropArea) return;
    // Not while an import's pictures are being sorted (figure-import).
    if (dropArea.closest(".is-sorting")) return;

    // Check if the paste is happening inside a Trix editor
    const activeElement = document.activeElement;
    if (activeElement && (activeElement.tagName === 'TRIX-EDITOR' || activeElement.closest('trix-editor'))) {
      return; // Let Trix handle the paste
    }

    const items = e.clipboardData.items;
    const imageItem = Array.from(items).find(item => item.type.startsWith("image/"));
    if (!imageItem) return;

    const file = imageItem.getAsFile();
    if (!file) return;

    const formData = new FormData();
    formData.append("image_file", file, file.name || "pasted-image.png");

    const token = document.querySelector('meta[name="csrf-token"]').getAttribute("content");

    const response = await fetch(dropArea.dataset.attachUrl, {
      method: "POST",
      headers: { "X-CSRF-Token": token },
      body: formData
    });

    if (response.ok) {
      const html = await response.text();
      const temp = document.createElement("template");
      temp.innerHTML = html;
      const newItem = temp.content.querySelector("li.sortable-item");
      if (newItem) {
        const el = document.getElementById("sortable-images");
        el.appendChild(newItem);
        initializeSortable();
        
        // Wait a moment for the DOM to update
        setTimeout(() => {
          // Find the new dimension element and start polling
          const newDimensionElement = newItem.querySelector('[data-image-dimensions]');
          if (newDimensionElement) {
            pollForDimensions(newDimensionElement).catch(error => {
              console.error('Error polling for dimensions:', error);
            });
          }
        }, 100);
      }
    } else {
      alert("Upload failed.");
    }
  };

  // Handle delete buttons with delegation. They're plain buttons (the
  // pictures sit inside the figure's form, which can't hold forms of its
  // own), carrying the picture's delete URL.
  const handleDelete = async (e) => {
    const button = e.target.closest(".delete-image");
    if (!button) return;

    e.preventDefault();

    if (!confirm("Delete this picture?")) return;

    const imageId = button.dataset.imageId;
    const token = document.querySelector('meta[name="csrf-token"]').getAttribute("content");

    const response = await fetch(button.dataset.url, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": token,
        "Accept": "application/json"
      }
    });

    if (response.ok) {
      const li = document.querySelector(`li[data-id='${imageId}']`);
      if (li) li.remove();
    } else {
      alert("Failed to delete image.");
    }
  };

  // Only bind handlers if they haven't been bound before
  if (!boundHandlers.has('dragover')) {
    document.addEventListener("dragover", handleDragOver);
    boundHandlers.add('dragover');
  }
  
  if (!boundHandlers.has('dragleave')) {
    document.addEventListener("dragleave", handleDragLeave);
    boundHandlers.add('dragleave');
  }
  
  if (!boundHandlers.has('drop')) {
    document.addEventListener("drop", handleDrop);
    boundHandlers.add('drop');
  }
  
  if (!boundHandlers.has('paste')) {
    document.addEventListener("paste", handlePaste);
    boundHandlers.add('paste');
  }
  
  if (!boundHandlers.has('submit')) {
    document.addEventListener("click", handleDelete);
    boundHandlers.add('submit');
  }
}

// Sortable initialization that needs to happen on each page load
function initializeSortable() {
  const el = document.getElementById('sortable-images');
  if (el && !el.sortable) {
    new Sortable(el, {
      animation: 150,
      onEnd: function () {
        const orderedIds = Array.from(el.children).map(item => item.dataset.id);
        fetch(el.dataset.reorderUrl, {
          method: "PATCH",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ ordered_ids: orderedIds })
        });
      }
    });
    el.sortable = true; // Mark as initialized
  }
}

// Function to check and update dimensions for a single element.
// The tile carries its own metadata URL: figure URLs use slugs, so the
// figure can't be read off the page's path.
async function checkDimensions(element) {
  const url = element.dataset.metadataUrl;
  if (!url) return false;

  try {
    const response = await fetch(url, { headers: { "Accept": "application/json" } });
    if (response.ok) {
      const data = await response.json();
      if (data.width && data.height) {
        element.textContent = `${data.width} × ${data.height} px`;
        return true;
      }
    }
  } catch (error) {
    console.error('Error fetching image metadata:', error);
  }
  return false;
}

// Function to poll for dimensions
async function pollForDimensions(element) {
  if (!element.querySelector('span.animate-pulse')) return;

  for (let i = 0; i < 10; i++) {
    if (await checkDimensions(element)) return;
    await new Promise(resolve => setTimeout(resolve, 1000));
  }
  element.textContent = "Size unknown";
}

// Initialize dimensions checking for all elements
async function initializeDimensions() {
  const dimensionElements = document.querySelectorAll('[data-image-dimensions]');
  await Promise.all(Array.from(dimensionElements).map(pollForDimensions));
}

// Export initialization functions
export function initializeReferenceImages() {
  // Set up one-time event listeners
  setupEventListeners();
  
  // Initialize Sortable
  initializeSortable();

  // Initialize dimensions checking
  initializeDimensions().catch(error => {
    console.error('Error initializing dimensions:', error);
  });
}

// Make initializeDimensions available globally for use after image uploads
window.initializeDimensions = initializeDimensions; 